import {
  insertExerciseSchema,
  patchExerciseSchema,
} from "@/api/db/schema/exercises";
import { exerciseService } from "@/api/services/exercise.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

// PUBLIC ENDPOINTS

export const listExercisesFn = createServerFn({ method: "GET" }).handler(
  async () => {
    const exercises = await exerciseService.listExercises();
    return {
      status: 200,
      body: {
        exercises,
        total: exercises.length,
      },
    };
  },
);

export const listExercisesOptions = () =>
  queryOptions({
    queryKey: ["listExercises"],
    queryFn: () => listExercisesFn(),
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

const getExerciseSchema = z.object({
  exerciseId: z.string(),
});

export const getExerciseFn = createServerFn({ method: "GET" })
  .inputValidator(getExerciseSchema)
  .handler(async ({ data }) => {
    const exercise = await exerciseService.getExerciseById(data.exerciseId);

    if (!exercise) {
      return {
        status: 404,
        body: { message: "Egzersiz bulunamadı" },
      };
    }

    return {
      status: 200,
      body: exercise,
    };
  });

const searchExercisesSchema = z.object({
  query: z.string().min(1),
});

export const searchExercisesFn = createServerFn({ method: "GET" })
  .inputValidator(searchExercisesSchema)
  .handler(async ({ data }) => {
    const exercises = await exerciseService.searchExercises(data.query);
    return {
      status: 200,
      body: {
        exercises,
        total: exercises.length,
      },
    };
  });

// ADMIN ENDPOINTS

export const listAllExercisesFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const exercises = await exerciseService.listAllExercises();
    return {
      status: 200,
      body: {
        exercises,
        total: exercises.length,
      },
    };
  });

export const listAllExercisesOptions = () =>
  queryOptions({
    queryKey: ["listAllExercises"],
    queryFn: () => listAllExercisesFn(),
  });

export const createExerciseFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertExerciseSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const exercise = await exerciseService.createExercise(data);

    return {
      status: 201,
      body: {
        message: "Egzersiz başarıyla oluşturuldu",
        exercise,
      },
    };
  });

export const updateExerciseFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchExerciseSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const { exerciseId, ...updateData } = data;
    const exercise = await exerciseService.updateExercise(
      exerciseId,
      updateData,
    );

    if (!exercise) {
      return {
        status: 404,
        body: { message: "Egzersiz bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: "Egzersiz güncellendi",
        exercise,
      },
    };
  });

const deleteExerciseSchema = z.object({
  exerciseId: z.string(),
});

export const deleteExerciseFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(deleteExerciseSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const exercise = await exerciseService.deleteExercise(data.exerciseId);

    if (!exercise) {
      return {
        status: 404,
        body: { message: "Egzersiz bulunamadı" },
      };
    }

    return {
      status: 200,
      body: { message: "Egzersiz silindi" },
    };
  });

const toggleActiveSchema = z.object({
  exerciseId: z.string(),
});

export const toggleExerciseActiveFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(toggleActiveSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const exercise = await exerciseService.toggleActive(data.exerciseId);

    if (!exercise) {
      return {
        status: 404,
        body: { message: "Egzersiz bulunamadı" },
      };
    }

    return {
      status: 200,
      body: {
        message: `Egzersiz ${exercise.isActive ? "aktifleştirildi" : "devre dışı bırakıldı"}`,
        exercise,
      },
    };
  });

// Excel import
const bulkImportSchema = z.object({
  exercises: z.array(insertExerciseSchema),
});

export const bulkImportExercisesFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(bulkImportSchema)
  .handler(async ({ context, data }) => {
    if (context.auth.role !== "admin") {
      return {
        status: 403,
        body: { message: "Forbidden" },
      };
    }

    const result = await exerciseService.bulkImportExercises(data.exercises);

    return {
      status: 200,
      body: {
        message: `${result.imported} egzersiz içe aktarıldı`,
        imported: result.imported,
        errors: result.errors,
      },
    };
  });
